<template>
  <div
      class="MESSAGE"
      selenium="message"
      :class="{ 'OWN': isOwnMessage, 'SYSTEM': isSystemMessage }">
    <transition :name="transitionDirection" mode="out-in" appear>
      <div
          class="BUBBLE"
          selenium="message-bubble"
          :class="{ 'OWN': isOwnMessage, 'SYSTEM': isSystemMessage }">
        <span v-if="message.message" :class="{ 'm-b-1': hasImages }">{{ message.message }}</span>
        <gallery v-if="hasImages" :files="messageAsset" />
      </div>
    </transition>
    <span class="text-md c-gray60">{{ timeCreated | timeOfDay }}</span>
  </div>
</template>

<script>

import { mapGetters } from 'vuex';
import http from '@homeservices/api-kit/axios';
import files from '@/mixins/job/files';
import addUTCTimeZoneDesignator from '@/utils/add-utc-time-zone-designator';

export default {
  name: 'message-bubble',
  mixins: [
    files,
  ],
  props: {
    message: Object,
    isCollapsed: Boolean,
  },
  computed: {
    ...mapGetters('user', [
      'userId',
    ]),
    // Used by files mixin.
    fileIds () {
      return this.message.asset_urls;
    },
    hasImages () {
      return this.message.asset_urls && this.message.asset_urls.length;
    },
    isSystemMessage () {
      return this.message.message_type === 'system';
    },
    isOwnMessage () {
      return !this.isSystemMessage && this.userId === this.message.sender_id;
    },
    transitionDirection () {
      if (this.isSystemMessage) {
        return 'NONE';
      }

      return this.isOwnMessage ? 'SCALE-RIGHT' : 'SCALE-LEFT';
    },
    messageAsset () {
      return this.message.asset_urls.map(url => ({ url }));
    },
    timeCreated () {
      return addUTCTimeZoneDesignator(this.message.time_created);
    },
  },
  watch: {
    isCollapsed: {
      immediate: true,
      handler (val) {
        if (!val) {
          if (!this.message.time_read && this.message.sender_type === 'consumer') {
            this.markMessageAsSeen();
          }
        }
      },
    },
  },
  methods: {
    async markMessageAsSeen () {
      try {
        await http.post(`/platform/workorder/r/workorder/prorders/messages/order-hash/${this.message.order_hash}/message-id/${this.message.message_id}/actions/see/v1`);
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools";

  @import "~@/views/Dashboard/constants";

  .MESSAGE {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-left: 2*$unit;
    padding: $unit 7*$unit $unit 0;
    backface-visibility: hidden;

    &.OWN {
      align-items: flex-end;
      margin-left: 0;
      margin-right: 2*$unit;
      padding-right: 0;
      padding-left: 7*$unit;
    }

    &.SYSTEM {
      align-items: center;
      margin: 0;
      padding-left: 2*$unit;
      padding-right: 2*$unit;
    }
  }

  .BUBBLE {
    position: relative;
    display: flex;
    flex-direction: column;
    padding: $unit;
    color: $color-gray70;
    background-color: $color-gray20;
    border-radius: 3px;
    white-space: pre-wrap;
    overflow-wrap: break-word;
    word-break: break-word;

    &:before {
      content: '';
      @include absolute(auto, auto, auto, -2*$unit);
      width: 2*$unit;
      height: 2*$unit;
      border-top: 1.5*$unit solid $color-gray20;
      border-left: 2*$unit solid transparent;
    }

    &.OWN {
      color: white;
      background-color: $color-depot;

      &:before {
        left: auto;
        right: -2*$unit;
        border-top: 1.5*$unit solid $color-depot;
        border-right: 2*$unit solid transparent;
        border-left: none;
      }
    }

    &.SYSTEM {
      @include text-medium;
      text-align: center;
      padding: 0;
      color: $color-gray60;
      background: none;
      border-radius: 0;

      &:before {
        display: none;
      }
    }
  }

  .SCALE-RIGHT-enter-active,
  .SCALE-RIGHT-leave-active,
  .SCALE-LEFT-enter-active,
  .SCALE-LEFT-leave-active {
    transform: none;
    will-change: transform;
    transform-origin: top right;
    transition: transform $transition-half-duration;

    &.SCALE-RIGHT-enter,
    &.SCALE-RIGHT-leave-to,
    &.SCALE-LEFT-enter,
    &.SCALE-LEFT-leave-to {
      transform: scale(0);
    }
  }

  .SCALE-LEFT-enter-active,
  .SCALE-LEFT-leave-active {
    transform-origin: top left;
  }
</style>
